// The facts behind the Your Agent page: pure functions, raw API payloads in, plain objects out. Nothing here
// renders, fetches or reads the clock on its own (`now` is a parameter), so every word and figure the page
// shows can be tested without mounting it.
//
// Facts are built from the other pages' own helpers, so a figure here can never disagree with the page it opens:
// the last-checked words come from the Connections page, the Alloy lines from the Alloy page's view helpers.
// Anything a source did not report stays null and reads "—" or an honest sentence on screen.

import { creditUnit, creditsPresentation, runwayDays, runwayLine } from "@/components/alloy/alloyView";
import {
  BRAND_SPECIFIC_SKILLS,
  SKILL_CATEGORIES,
  categorizeSkill,
} from "@/components/agent/skillTaxonomy";
import { countUpdatesSince, formatDayLabel, formatLastUsed, latestChangeAt, matchesSkillSearch, parseTimestamp } from "@/components/agent/skillUpdates";

import { customerSkillTitle, isClientVisibleSkill } from "@/components/agent/skillPresentation";

const LOW_RUNWAY_DAYS = 7;

/** Active skills in the three canonical groups, narrowed by a search, sorted by name. */
export function groupActiveSkills(skills, search = "") {
  const groups = Object.fromEntries(SKILL_CATEGORIES.map((category) => [category, []]));
  for (const skill of skills ?? []) {
    if (String(skill?.status ?? "").toLowerCase() !== "active") continue;
    if (!isClientVisibleSkill(skill) || !matchesSkillSearch(skill, search)) continue;
    groups[categorizeSkill(skill)].push(skill);
  }
  return SKILL_CATEGORIES.map((category) => ({
    category,
    skills: groups[category].sort((left, right) => customerSkillTitle(left).localeCompare(customerSkillTitle(right))),
  }));
}

/**
 * What the agent can do, as counts, how those skills split across their three sources, and when a skill last
 * changed ("Today", "Yesterday", a date; null when no change has been reported). Usage, from the active skills a
 * client can see: when one was last used (null when none reported a use) and up to three most used names.
 */
export function skillFacts(skills, changes, now = new Date()) {
  const groups = groupActiveSkills(skills);
  const active = groups.reduce((total, group) => total + group.skills.length, 0);
  const visible = groups.flatMap((group) => group.skills);
  const clientChanges = (changes ?? []).filter(isClientVisibleSkill);
  const latest = latestChangeAt(clientChanges);
  let latestUse = null;
  for (const skill of visible) {
    const used = parseTimestamp(skill.lastUsedAt);
    if (used && (!latestUse || used > latestUse)) latestUse = used;
  }
  return {
    active,
    brandSpecific: groups.find((group) => group.category === BRAND_SPECIFIC_SKILLS)?.skills.length ?? 0,
    updatedThisWeek: countUpdatesSince(clientChanges, { days: 7, now }),
    lastUpdate: latest ? formatDayLabel(latest, now) : null,
    split: active > 0
      ? groups.map((group) => ({ key: group.category, count: group.skills.length, percent: Math.round((group.skills.length / active) * 100) }))
      : [],
    lastUsed: formatLastUsed(latestUse, now),
    mostUsed: visible
      .filter((skill) => skill.useCount > 0)
      .sort((left, right) => right.useCount - left.useCount || customerSkillTitle(left).localeCompare(customerSkillTitle(right)))
      .slice(0, 3)
      .map(customerSkillTitle),
  };
}

function plural(count, one, many = `${one}s`) {
  return `${count} ${count === 1 ? one : many}`;
}

function listNames(names) {
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
}

// A key with no automatic check is either on the agent or it is not; the Connections page calls it "Saved", and
// it counts as working here.
function connectionState(card) {
  if (card?.kind === "api_key" && card?.canTest === false && card?.status === "not_verified") return "saved";
  return ["connected", "not_working", "not_verified"].includes(card?.status) ? card.status : "not_verified";
}

/** The agent's connections as one state: how many work, which do not, and when they were last checked. */
export function connectionFacts(payload, { describeChecked = () => "", now = new Date() } = {}) {
  if (!payload || typeof payload !== "object") return null;
  const cards = (Array.isArray(payload.connections) ? payload.connections : []).map((card) => ({ name: card.label || card.app, state: connectionState(card) }));
  const named = (state) => cards.filter((card) => card.state === state).map((card) => card.name).sort((left, right) => left.localeCompare(right));
  const broken = named("not_working");
  const unverified = named("not_verified");
  const working = cards.length - broken.length - unverified.length;
  const total = cards.length;
  return {
    total,
    working,
    broken,
    unverified,
    pending: Array.isArray(payload.pending) ? payload.pending.length : 0,
    tone: broken.length ? "bad" : unverified.length ? "warn" : total ? "ok" : "none",
    word: total === 0 ? "None yet" : broken.length === 0 && unverified.length === 0 ? "All working" : `${working} of ${total} working`,
    checked: payload.lastSweepAt ? describeChecked(payload.lastSweepAt, now) : null,
  };
}

/** GrowthOS Alloy in the agent's terms: is it on, what is left, and how long it lasts. */
export function alloyFacts(summary, now = new Date()) {
  if (!summary || typeof summary !== "object") return null;
  const view = creditsPresentation(summary);
  const unit = creditUnit(summary);
  const balance = Number(summary.balanceUsd || 0);
  const days = runwayDays(summary, now);
  const out = view.on && !(balance > 0);
  const low = view.on && !out && days !== null && days < LOW_RUNWAY_DAYS;
  return {
    configured: summary.configured !== false,
    on: view.on,
    pending: view.pending,
    blocker: view.blocker,
    balanceUsd: balance,
    // Alloy tokens for a brand on tokens, as its Alloy page shows them; dollars for everyone else.
    balance: unit.labelled(balance),
    tokens: unit.tokens,
    // The Alloy page's own runway sentence, so the two can never disagree.
    runway: runwayLine(summary, now),
    out,
    low,
    // The Alloy page shows the reason; here it is only a thing to go and fix.
    rechargeFailed: Boolean(summary.autoRecharge?.enabled && summary.autoRecharge?.lastError),
  };
}

const ROLE_WORDS = { owner: "Owner", admin: "Admin", member: "Member" };

/** Who can work with the agent. */
export function teamFacts(payload) {
  if (!payload || typeof payload !== "object") return null;
  const members = (Array.isArray(payload.members) ? payload.members : []).filter((member) => member && member.state !== "revoked");
  const byRole = {};
  for (const member of members) {
    const role = ROLE_WORDS[String(member.role || "").toLowerCase()] || "Member";
    byRole[role] = (byRole[role] || 0) + 1;
  }
  return {
    total: members.length,
    roles: ["Owner", "Admin", "Member"].filter((role) => byRole[role]).map((role) => plural(byRole[role], role.toLowerCase())),
    canManage: payload.accessManagement?.status === "available" && payload.accessManagement?.canManage === true,
  };
}

/** Use the configured name when available, otherwise a neutral label. */
export function aboutFacts(profile, workspaceName) {
  return {
    name: profile?.agent?.name?.trim() || "Your agent",
    imageUrl: profile?.agent?.imageUrl ?? null,
    workspace: workspaceName,
  };
}

/**
 * What the client can fix themselves, most urgent first: credits, then connections. Each issue names the thing,
 * says what to do in one sentence, and carries the way to the page that fixes it. Several connections in the
 * same state share one issue. Anything only the GrowthOS team can fix (a setup blocker) is not listed here.
 */
export function clientIssues({ connections, alloy }) {
  const issues = [];
  if (alloy?.on && alloy.out) {
    issues.push({ key: "out-of-credits", kind: "credits", tone: "bad", title: "Out of tokens", text: "Your agent cannot answer until tokens are added. It picks up where it left off.", action: { to: "/alloy", label: "Add credits" } });
  }
  if (connections?.broken.length) {
    const one = connections.broken.length === 1;
    issues.push({
      key: "broken-connections",
      kind: "connections",
      tone: "bad",
      title: one ? `${connections.broken[0]} is not working` : `${connections.broken.length} connections are not working`,
      text: one ? "Reconnect it so your agent can use it again." : `${listNames(connections.broken)}. Reconnect them so your agent can use them again.`,
      action: { to: "/connections", label: one ? "Reconnect" : "Fix connections" },
    });
  }
  if (alloy?.on && alloy.rechargeFailed) {
    issues.push({ key: "recharge-failed", kind: "credits", tone: "warn", title: "Auto-recharge failed", text: "Your last automatic top-up did not go through. Top up once by hand so your agent keeps running.", action: { to: "/alloy", label: "Open Alloy" } });
  }
  if (alloy?.on && alloy.low) {
    issues.push({ key: "low-credits", kind: "credits", tone: "warn", title: "Tokens are running low", text: `${alloy.balance} left. ${alloy.runway}.`, action: { to: "/alloy", label: "Add credits" } });
  }
  if (connections?.unverified.length) {
    const one = connections.unverified.length === 1;
    issues.push({
      key: "untested-connections",
      kind: "connections",
      tone: "warn",
      title: one ? `${connections.unverified[0]} has not been tested` : `${connections.unverified.length} connections have not been tested`,
      text: one ? "Run a test so you know your agent can use it." : `${listNames(connections.unverified)}. Run a test so you know your agent can use them.`,
      action: { to: "/connections", label: one ? "Test it" : "Test connections" },
    });
  }
  return issues;
}

/** The agent's state in one word for its card: the worst thing on the list, or all clear. */
export function issuesTone(issues) {
  if (issues.some((issue) => issue.tone === "bad")) return "bad";
  return issues.length ? "warn" : "ok";
}
